/**
 * StripePaymentPopup — Popup brand Mytenue pour confirmations Stripe.
 *
 * v1.12.646 — REMPLACE showAppAlert (AppModal) pour les popups post-Stripe.
 * Pourquoi: showAppAlert utilise AppModal+Portal+pushState history qui causait
 * 2 problèmes:
 *   1. Click OK traversait vers PremiumModal empilée derrière (Portal stacking)
 *   2. history.back au cleanup ramenait à l'URL Stripe précédente
 *
 * Cette popup:
 *   - Portal vers document.body MAIS rendue en DERNIER (zIndex max int32)
 *   - PAS de pushState/history.back (donc aucun risque de race condition)
 *   - PAS de TouchableOpacity backdrop (donc aucun risque de click traversant)
 *   - State contrôlé via DeviceEventEmitter event (pas de promise/refs partagés)
 *
 * Usage:
 *   import { showStripePopup } from './StripePaymentPopup';
 *   showStripePopup('Paiement réussi', 'Votre abonnement est actif.');
 */

import React, { useEffect, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform, DeviceEventEmitter } from 'react-native';

let createPortal = null;
if (Platform.OS === 'web') {
  try { createPortal = require('react-dom').createPortal; } catch {}
}

// API impérative
export function showStripePopup(title, body) {
  DeviceEventEmitter.emit('__stripe_popup_show__', { title, body });
}

// Composant à monter UNE FOIS dans App.js (cf. AppDialogHost)
export function StripePaymentPopupHost() {
  const [popup, setPopup] = useState(null); // { title, body } | null

  useEffect(() => {
    const sub = DeviceEventEmitter.addListener('__stripe_popup_show__', (data) => {
      setPopup(data || null);
    });
    return () => sub.remove();
  }, []);

  if (!popup || Platform.OS !== 'web') return null;

  // v1.12.647 — Fix click-through: setTimeout 100ms pour fermer la popup
  // APRÈS mouseup. Sinon mousedown capturé par notre bouton, popup disparaît
  // sur setPopup(null), mouseup atterrit sur l'élément derrière (PremiumModal
  // Subscribe button) → startCheckout. Confirmé dans stack trace.
  const handleClose = () => {
    setTimeout(() => setPopup(null), 100);
  };

  const tree = (
    <View style={s.overlay} pointerEvents="auto">
      {/* Backdrop opaque qui capture aussi les clicks (évite click-through) */}
      <View
        style={s.backdropClickGuard}
        // @ts-ignore - propriétés DOM pour bloquer le click-through
        onMouseDown={(e) => { try { e.preventDefault(); e.stopPropagation(); } catch {} }}
        onMouseUp={(e) => { try { e.preventDefault(); e.stopPropagation(); } catch {} }}
      />
      <View style={s.card}>
        {!!popup.title && <Text style={s.title}>{popup.title}</Text>}
        {!!popup.body && <Text style={s.body}>{popup.body}</Text>}
        <TouchableOpacity
          onPress={handleClose}
          activeOpacity={0.85}
          style={s.btn}
        >
          <Text style={s.btnText}>OK</Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  // Portal vers document.body : rendu APRÈS tous les autres Portals existants
  // (PremiumModal etc.) → naturellement au-dessus dans le DOM.
  // zIndex max int32 garantit le top-most dans tous les stacking contexts.
  if (createPortal && typeof document !== 'undefined' && document.body) {
    return createPortal(tree, document.body);
  }
  return tree;
}

const s = StyleSheet.create({
  overlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.55)',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2147483647, // max int32 — au-dessus de TOUT
    padding: 20,
  },
  // v1.12.647 — Backdrop dédié sous la card qui absorbe tous les clicks
  // (sinon click-through vers PremiumModal Subscribe derrière)
  backdropClickGuard: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    zIndex: 1, // sous la card mais au-dessus du fond
  },
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    paddingTop: 28,
    paddingBottom: 20,
    paddingHorizontal: 24,
    width: '100%',
    maxWidth: 420,
    shadowColor: '#000',
    shadowOpacity: 0.25,
    shadowRadius: 30,
    shadowOffset: { width: 0, height: 10 },
    elevation: 12,
    zIndex: 2, // au-dessus du backdropClickGuard
  },
  title: {
    fontSize: 19,
    fontWeight: '700',
    color: '#1A1814',
    textAlign: 'center',
    marginBottom: 12,
    letterSpacing: 0.2,
  },
  body: {
    fontSize: 14,
    color: '#5F584F',
    lineHeight: 21,
    textAlign: 'center',
    marginBottom: 24,
    whiteSpace: 'pre-line',
  },
  btn: {
    backgroundColor: '#9B7B5A',
    paddingVertical: 13,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  btnText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '700',
    letterSpacing: 0.5,
  },
});

export default StripePaymentPopupHost;
